import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import SEO from "../../public/seo-routes.json";
import BLOG from "../../public/blog.json";
import NICHES from "../../public/niches.json";
import { LANGS, stripLang, withLang, hasTranslation, langFromPath } from "@/i18n";

type Entry = { lang: string; title: string; description: string };
/* Trei surse, un singur tabel. Paginile fixe sunt scrise de mână în
   seo-routes.json; articolele și paginile de nișă își aduc meta din blog.json
   și niches.json, calculate de compilatoare din chiar titlul și rezumatul lor.
   O pagină nouă apare aici fără să atingă nimeni un fișier de configurare.

   Tabelul ăsta trebuie să conțină exact aceleași rute ca cel din
   server/src/seo.ts. Când n-a fost așa — niches.json cablat pe server și uitat
   aici — serverul trimitea titlul corect, iar clientul îl suprascria cu „404"
   la hidratare: pagina se citea perfect, dar tab-ul spunea că nu există. */
/** Expus pentru testul care verifică acoperirea. Vezi src/test/seo-routes.test.ts. */
export const ROUTES = {
  ...(SEO.routes as Record<string, Entry>),
  ...(BLOG.routes as Record<string, Entry>),
  ...(NICHES.routes as Record<string, Entry>),
};
const SITE = SEO.site;
const IMAGE = SEO.site + SEO.defaultImage;

/** Meta pe rută, actualizată la navigare.
 *
 *  Partea asta e pentru browser și pentru Google, care randează JavaScript.
 *  Crawlerele de social nu îl randează, deci ele primesc meta corectă din
 *  index.html, injectată de server — vezi server/src/seo.ts. Ambele citesc
 *  același public/seo-routes.json, ca să nu poată devia una de alta. */
export function useRouteSeo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const entry = ROUTES[pathname];
    const lang = entry?.lang ?? langFromPath(pathname);
    // O rută necunoscută înseamnă 404: nu-i împrumut titlul paginii principale,
    // fiindcă ar face-o să pară o pagină reală în rezultatele de căutare.
    const title = entry ? entry.title : "404 — coresmith.dev";
    const description = entry
      ? entry.description
      : "That route doesn't match anything on coresmith.dev.";
    const url = SITE + (pathname === "/" ? "" : pathname);

    document.title = title;
    document.documentElement.lang = lang;
    setMeta("name", "description", description);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", IMAGE);
    setMeta("property", "og:locale", lang);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setLink("canonical", url);
    // Paginile fără intrare proprie nu au ce să caute în index.
    setMeta("name", "robots", entry ? "index,follow" : "noindex,follow");

    /* hreflang. Se pune doar pe paginile care există în toate trei limbile:
       pe /privacy, care e doar în engleză, o listă de alternative ar promite
       traduceri inexistente, iar Google raportează asta ca eroare. */
    const bare = stripLang(pathname);
    const alts = hasTranslation(bare)
      ? [
          ...LANGS.map((l) => ({ hreflang: l, href: SITE + withLang(l, bare) })),
          { hreflang: "x-default", href: SITE + bare },
        ]
      : [];
    setAlternates(alts);
  }, [pathname]);
}

function setMeta(attr: "name" | "property", key: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

/** Înlocuiește tot setul de alternative. Adăugarea fără ștergere ar lăsa, după
 *  două navigări, alternativele paginii precedente amestecate cu ale celei
 *  curente — iar Google le-ar citi pe toate. */
function setAlternates(list: Array<{ hreflang: string; href: string }>) {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
  for (const a of list) {
    const el = document.createElement("link");
    el.rel = "alternate";
    el.hreflang = a.hreflang;
    el.href = a.href;
    document.head.appendChild(el);
  }
}
